// Pure helpers describing what buckets mean in connected tools (badges, confirm texts, the delete
// dialog). Deck stacks ARE buckets ("native"); each bound To Do list can be one bucket ("lists").
import type { Bucket, Item, Manifest } from "../api/types";
import { connectorName } from "./archive";
import { connectorLabel } from "./format";

export type BucketCapability = "none" | "native" | "lists";

/** Fallback when a manifest doesn't declare `capabilities.buckets`. */
const KNOWN: Record<string, BucketCapability> = { "nextcloud-deck": "native", "msgraph-todo": "lists" };

export function bucketCapability(type: string, manifests?: Manifest[] | null): BucketCapability {
  const caps = manifests?.find((m) => m.connector_type === type)?.capabilities;
  if (caps && !Array.isArray(caps) && typeof caps === "object") {
    const v = (caps as Record<string, unknown>).buckets;
    if (v === "native" || v === "lists" || v === "none") return v;
  }
  return KNOWN[type] ?? "none";
}

/** Minimal binding shape (Binding and BindingSummary both fit). */
export interface BindingLike {
  id: string;
  connector_type: string;
  remote_collection_name: string;
}

/**
 * Lines for the confirm shown before enabling buckets on a collection with bindings; empty when
 * no bound tool is affected (then no confirm is needed).
 */
export function enableBucketsNotes(bindings: BindingLike[], manifests?: Manifest[] | null): string[] {
  const notes: string[] = [];
  const seen = new Set<BucketCapability>();
  for (const b of bindings) {
    const cap = bucketCapability(b.connector_type, manifests);
    if (cap === "none" || seen.has(cap)) continue;
    seen.add(cap);
    const tool = connectorName(b.connector_type, manifests);
    notes.push(
      cap === "native"
        ? `${tool}: stacks become buckets. A card's status then follows only its done flag.`
        : `${tool}: each bound list is one bucket. Moving a card between buckets moves the task between lists.`,
    );
  }
  return notes;
}

export interface BucketBadge {
  key: string;
  label: string;
  title: string;
}

/** Header badges of a bucket: "Deck" for a stack, "To Do list: Name" for a bound list. */
export function bucketBadges(bucket: Bucket, bindings: BindingLike[], manifests?: Manifest[] | null): BucketBadge[] {
  return (bucket.remote ?? []).map((r) => {
    const tool = connectorName(r.connector_type, manifests);
    if (bucketCapability(r.connector_type, manifests) === "lists") {
      const list = bindings.find((b) => b.id === r.binding_id)?.remote_collection_name ?? bucket.name;
      return {
        key: r.binding_id,
        label: `${connectorLabel(r.connector_type)} list: ${list}`,
        title: `This bucket is the ${tool} list “${list}”`,
      };
    }
    return { key: r.binding_id, label: connectorLabel(r.connector_type), title: `${tool} stack (synced)` };
  });
}

/** What deleting a bucket does remotely, one sentence per linked stack / list. */
export function bucketDeleteEffects(bucket: Bucket, bindings: BindingLike[], manifests?: Manifest[] | null): string[] {
  return (bucket.remote ?? []).map((r) => {
    const tool = connectorName(r.connector_type, manifests);
    if (bucketCapability(r.connector_type, manifests) === "lists") {
      const list = bindings.find((b) => b.id === r.binding_id)?.remote_collection_name ?? bucket.name;
      return `The ${tool} list “${list}” is unlinked but not deleted.`;
    }
    return `The ${tool} stack is deleted once it is empty.`;
  });
}

export interface DeleteBucketPlan {
  /** Items that have to move (all items in the bucket, subtasks included). */
  count: number;
  /** Move targets: the other buckets in order, then "No bucket" (value null). */
  targets: { value: string | null; label: string }[];
  /** Pre-selected target: the neighbouring bucket (previous, else next), else no bucket. */
  defaultTarget: string | null;
  remote: string[];
}

export function deleteBucketPlan(
  bucket: Bucket,
  buckets: Bucket[],
  items: Item[],
  bindings: BindingLike[],
  manifests?: Manifest[] | null,
): DeleteBucketPlan {
  const ordered = [...buckets].sort((a, b) => a.position - b.position);
  const idx = ordered.findIndex((b) => b.id === bucket.id);
  const others = ordered.filter((b) => b.id !== bucket.id);
  const neighbour = idx > 0 ? ordered[idx - 1] : ordered[idx + 1];
  return {
    count: items.filter((i) => i.bucket_id === bucket.id).length,
    targets: [...others.map((b) => ({ value: b.id, label: b.name })), { value: null, label: "No bucket" }],
    defaultTarget: neighbour?.id ?? null,
    remote: bucketDeleteEffects(bucket, bindings, manifests),
  };
}

/** Match a `bucket:Name` token value (dashes for spaces, case-insensitive) to a bucket. */
export function matchBucket(buckets: Bucket[], token: string): Bucket | undefined {
  const norm = (s: string) => s.trim().toLowerCase().replace(/[\s-]+/g, " ");
  const t = norm(token);
  return buckets.find((b) => norm(b.name) === t);
}
